import {
  IconArrowRight,
  IconBulb,
  IconCheck,
  IconCode,
  IconFlask,
  IconHammer,
  IconPencil,
  IconRocket,
  IconRoute,
  IconScale,
  IconUser,
  IconVectorTriangle,
} from "@tabler/icons-react";
import "./Process.css";
import { openBooking } from "../../utils/booking";
import { useRef } from "react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { autoplay, token, travel, press, Cursor, Arrows, NodeLabel, AiNode, aiThink, popIn } from "../Board/Board";
gsap.registerPlugin(useGSAP);

// Each card's media is a tiny Miro-style board that acts out a step of the process.
// Boards loop on their own while on screen and pause off screen.

// 01 Framing: a pile of stickies snaps into a flowchart
const FRAME_Y = 130;
const FRAME_NODES = [
  { label: "Idea", icon: IconBulb, type: "note", mess: { x: 92, y: 72, rotation: -12 }, x: 50 },
  { label: "Map", icon: IconRoute, type: "process", mess: { x: 298, y: 60, rotation: 9 }, x: 150 },
  { label: "Feasible?", icon: IconScale, type: "decision", mess: { x: 140, y: 196, rotation: 7 }, x: 250, decision: true },
  { label: "Build", icon: IconHammer, type: "process", mess: { x: 292, y: 200, rotation: -6 }, x: 350 },
];
const FRAME_GAP = 38; // half a sticky plus breathing room for the arrowhead

const FramingDemo = () => {
  const ref = useRef(null);

  useGSAP(() => {
    const q = gsap.utils.selector(ref);
    q(".board-node").forEach((n, i) => gsap.set(n, { transformOrigin: "50% 50%", ...FRAME_NODES[i].mess }));
    q(".board-line").forEach((l) => gsap.set(l, { attr: { x2: l.getAttribute("x1") }, opacity: 0 }));
    gsap.set(".board-cursor", { x: 196, y: 222 });

    // Plays forward (mess -> flow), holds, then rewinds back to the mess
    const tl = gsap.timeline({ paused: true, repeat: -1, yoyo: true, repeatDelay: 1.6 });
    tl.to(".board-cursor", { x: 196, y: 140, duration: 0.6, ease: "power3.inOut" });
    press(tl);
    q(".board-node").forEach((n, i) =>
      tl
        .to(n, { x: FRAME_NODES[i].x, y: FRAME_Y, rotation: 0, duration: 0.7, ease: "back.out(1.6)" }, 0.75 + i * 0.08)
        .to(n.querySelector("rect"), { fill: token(`--node-${FRAME_NODES[i].type}`), duration: 0.4 }, 1.2 + i * 0.08)
    );
    tl
      .to(".board-node--decision rect", { rotation: 45, scale: 0.8, transformOrigin: "50% 50%", duration: 0.5, ease: "back.out(2)" }, 1.1)
      .to(".board-cursor", { x: 262, y: 168, duration: 0.6, ease: "power3.inOut" }, 1);
    q(".board-line").forEach((l, i) =>
      tl.to(l, { attr: { x2: l.dataset.x2 }, opacity: 1, duration: 0.3, ease: "power2.out" }, 1.4 + i * 0.18)
    );
    autoplay(ref.current, tl);
  }, { scope: ref });

  return (
    <div ref={ref} className="board process-board" role="img" aria-label="Sticky notes sorting into a flowchart">
      <svg viewBox="0 0 400 260" aria-hidden="true">
        <Arrows id="framing" />
        {FRAME_NODES.slice(1).map((n, i) => (
          <line
            key={n.label}
            className="board-line board-line--on"
            x1={FRAME_NODES[i].x + FRAME_GAP}
            data-x2={n.x - FRAME_GAP}
            y1={FRAME_Y}
            y2={FRAME_Y}
            markerEnd="url(#framing-arrow-on)"
          />
        ))}
        {FRAME_NODES.map((n) => (
          <g key={n.label} className={`board-node${n.decision ? " board-node--decision" : ""}`}>
            <g className="board-pop board-filled type-note">
              <rect x="-32" y="-32" width="64" height="64" rx="2" />
              <NodeLabel icon={n.icon} dark>{n.label}</NodeLabel>
            </g>
          </g>
        ))}
        <Cursor />
      </svg>
    </div>
  );
};

// 02 Interaction: the AI drafts, you stay in control.
// Lap: draft reaches you -> you send it back -> AI redrafts -> you approve.
const BRANCHES = {
  apply: { label: "Apply", icon: IconCheck, type: "terminal", y: 60, path: "M190 94 V60 H288", route: "M190 130 V60 H330" },
  revise: { label: "Revise", icon: IconPencil, type: "process", y: 200, path: "M190 166 V200 H288", route: "M190 130 V200 H330" },
};
const AI_ROUTES = {
  in: "M60 130 H190",
  redraft: "M330 200 V244 H60 V130",
};

const InteractionDemo = () => {
  const ref = useRef(null);

  useGSAP(() => {
    const q = gsap.utils.selector(ref);
    const route = (name) => q(`[data-route=${name}]`)[0];
    const choose = (key) =>
      q("[data-branch]").forEach((el) => {
        const on = el.dataset.branch === key;
        el.classList.toggle("is-on", on);
        if (el.tagName === "path") el.setAttribute("marker-end", `url(#interaction-arrow${on ? "-on" : ""})`);
      });
    const pop = (target) =>
      tl.fromTo(target, { scale: 0.8 }, { scale: 1, duration: 0.5, ease: "back.out(3)", immediateRender: false });
    // You pick a branch: cursor clicks the option, then the draft follows it
    const decide = (key) => {
      tl.to(".board-cursor", { x: 340, y: BRANCHES[key].y + 14, duration: 0.6, ease: "power3.inOut" });
      press(tl);
      travel(tl, route(key));
      tl.call(() => choose(key));
      popIn(tl, q(`.board-out--${key}`)[0]);
    };

    gsap.set(".board-token", { x: 60, y: 130 });
    gsap.set(".board-cursor", { x: 228, y: 150 });
    gsap.set(".board-ai-node", { transformOrigin: "50% 50%" });

    const tl = gsap.timeline({ paused: true, repeat: -1 }).call(() => choose(null));
    travel(tl, route("in"));
    decide("revise");
    travel(tl, route("redraft"));
    tl.call(() => choose(null));
    pop(".board-ai-node");
    tl.to({}, { duration: 0.6 }); // AI reworks the draft
    travel(tl, route("in"));
    decide("apply");
    tl
      .to({}, { duration: 1.4 }) // hold so the approval reads
      .to(".board-cursor", { x: 228, y: 150, duration: 0.6, ease: "power3.inOut" })
      .set(".board-token", { x: 60, y: 130 });

    const think = aiThink();
    autoplay(ref.current, tl, think);
  }, { scope: ref });

  return (
    <div ref={ref} className="board process-board" role="img" aria-label="The AI drafts, you send it back, it redrafts, you approve">
      <svg viewBox="0 0 400 260" aria-hidden="true">
        <Arrows id="interaction" />
        <line className="board-line board-line--on" x1="100" y1="130" x2="152" y2="130" markerEnd="url(#interaction-arrow-on)" />
        {Object.entries(BRANCHES).map(([key, b]) => (
          <path key={key} data-branch={key} d={b.path} className="board-line" markerEnd="url(#interaction-arrow)" />
        ))}
        <path data-branch="revise" d="M330 226 V244 H60 V164" className="board-line board-line--dashed" markerEnd="url(#interaction-arrow)" />
        <text x="195" y="234" className="board-text board-text--muted">redraft</text>
        {Object.entries({ ...AI_ROUTES, apply: BRANCHES.apply.route, revise: BRANCHES.revise.route }).map(([name, d]) => (
          <path key={name} className="board-route" data-route={name} d={d} />
        ))}
        <circle className="board-token" r="5" />
        <g transform="translate(60 130)">
          <AiNode id="interaction" />
        </g>
        <g transform="translate(190 130)">
          <g className="board-pop board-filled type-decision">
            <polygon className="board-decision" points="0,-36 36,0 0,36 -36,0" />
            <NodeLabel icon={IconUser} dark>You</NodeLabel>
          </g>
        </g>
        {Object.entries(BRANCHES).map(([key, b]) => (
          <g key={key} transform={`translate(330 ${b.y})`}>
            <g className={`board-out--${key}`}>
              <g className={`board-pop board-outline board-choice type-${b.type}`} data-branch={key}>
                <rect x="-40" y="-26" width="80" height="52" rx="2" />
                <NodeLabel icon={b.icon}>{b.label}</NodeLabel>
              </g>
            </g>
          </g>
        ))}
        <Cursor />
      </svg>
    </div>
  );
};

// 03 Execution: the build fails a test, loops back, then ships
// 2x2 closed loop: Prototype (TL) -> Build (TR) -> Test (BR) -> Ship (BL) -> back up.
// Every lap ends where the next begins, so it repeats seamlessly.
const EXEC_ROUTES = {
  build: "M100 70 H300",
  test: "M300 70 V190",
  fix: "M300 190 H336 C372 190, 372 70, 336 70 H300",
  ship: "M300 190 H100",
  learn: "M100 190 V70",
};

const ExecutionDemo = () => {
  const ref = useRef(null);

  useGSAP(() => {
    const q = gsap.utils.selector(ref);
    const route = (name) => q(`[data-route=${name}]`)[0];
    const test = q(".board-test")[0];
    const ship = q(".board-ship")[0];
    gsap.set(".board-cursor", { x: 132, y: 92 });
    gsap.set(".board-token", { x: 100, y: 70 });
    gsap.set(".board-proto", { transformOrigin: "50% 50%" });

    const tl = gsap.timeline({ paused: true, repeat: -1 }).call(() => ship.classList.remove("is-live"));
    press(tl, 0.1);
    tl.fromTo(".board-proto", { scale: 1 }, { scale: 0.9, yoyo: true, repeat: 1, duration: 0.1, transformOrigin: "50% 50%" }, "<");
    travel(tl, route("build"));
    travel(tl, route("test"));
    tl
      .call(() => test.classList.add("is-fail"))
      .to(test, { x: "+=4", yoyo: true, repeat: 5, duration: 0.05 })
      .call(() => test.classList.remove("is-fail"), null, "+=0.2");
    travel(tl, route("fix"));
    travel(tl, route("test"));
    travel(tl, route("ship"));
    tl.call(() => ship.classList.add("is-live"));
    popIn(tl, ship);
    tl
      .to(".board-cursor", { x: 144, y: 212, duration: 0.6, ease: "power3.inOut" }, "<")
      .to({}, { duration: 0.6 }); // let "shipped" land before feedback flows back
    travel(tl, route("learn"));
    tl.to(".board-cursor", { x: 132, y: 92, duration: 0.8, ease: "power3.inOut" }, "<");
    autoplay(ref.current, tl);
  }, { scope: ref });

  return (
    <div ref={ref} className="board process-board" role="img" aria-label="A build loop: prototype, build, test, ship, learn, repeat">
      <svg viewBox="0 0 400 260" aria-hidden="true">
        <Arrows id="execution" />
        <line className="board-line" x1="137" y1="70" x2="263" y2="70" markerEnd="url(#execution-arrow)" />
        <line className="board-line" x1="300" y1="98" x2="300" y2="152" markerEnd="url(#execution-arrow)" />
        <path className="board-line board-line--dashed" d="M336 190 C372 190, 372 70, 337 70" markerEnd="url(#execution-arrow)" />
        <text x="382" y="130" className="board-text board-text--muted">fix</text>
        <line className="board-line" x1="264" y1="190" x2="142" y2="190" markerEnd="url(#execution-arrow)" />
        <line className="board-line board-line--dashed" x1="100" y1="162" x2="100" y2="105" markerEnd="url(#execution-arrow)" />
        <text x="76" y="131" className="board-text board-text--muted">learn</text>
        {Object.entries(EXEC_ROUTES).map(([name, d]) => (
          <path key={name} className="board-route" data-route={name} d={d} />
        ))}
        <circle className="board-token" r="5" />
        <g transform="translate(100 70) rotate(-3)">
          <g className="board-proto">
            <g className="board-pop board-filled type-note">
              <rect x="-35" y="-30" width="70" height="60" rx="2" />
              <NodeLabel icon={IconVectorTriangle} dark>Prototype</NodeLabel>
            </g>
          </g>
        </g>
        <g transform="translate(300 70)">
          <g className="board-pop board-filled type-process">
            <rect x="-35" y="-26" width="70" height="52" rx="2" />
            <NodeLabel icon={IconCode} dark>Build</NodeLabel>
          </g>
        </g>
        <g transform="translate(300 190)">
          <g className="board-test">
            <g className="board-pop board-filled type-decision">
              <polygon points="0,-36 36,0 0,36 -36,0" />
              <NodeLabel icon={IconFlask} dark>Test</NodeLabel>
            </g>
          </g>
        </g>
        <g transform="translate(100 190)">
          <g className="board-ship">
            <g className="board-pop board-outline type-terminal">
              <rect x="-40" y="-26" width="80" height="52" rx="26" />
              <NodeLabel icon={IconRocket}>Ship</NodeLabel>
            </g>
          </g>
        </g>
        <Cursor />
      </svg>
    </div>
  );
};

const steps = [
  {
    label: "Find",
    sentence: "I watch your users work and find where the tool loses them, or shape an idea into a scoped first version.",
    bullets: ["User sessions and workflow map", "Trust and control review", "Ranked fix list or v1 scope"],
    Demo: FramingDemo,
  },
  {
    label: "Test",
    sentence: "The priority flow goes in front of your users as a prototype before you commit to a build.",
    bullets: ["AI interaction model", "Figma prototype or React frontend", "Go, change or stop call"],
    Demo: InteractionDemo,
  },
  {
    label: "Ship",
    sentence: "A first version on staging, in your repository, with analytics for the metrics we agreed.",
    bullets: ["React or Next.js on your API", "Design system in code and Figma", "2 weeks of post-launch fixes"],
    Demo: ExecutionDemo,
  },
];

// Same layout as the Work cards: 4:3 board on top, text below, no box
function ProcessCard({ step }) {
  return (
    <article className="process-card">
      <step.Demo />

      <div className="stack stack--tight">
        <h3 className="h3">{step.label}</h3>
        <p className="text process-card-sentence">{step.sentence}</p>
      </div>

      <ul className="list list--squares">
        {step.bullets.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>

      <button type="button" className="link-cta" onClick={() => openBooking()}>
        Let's talk <IconArrowRight size={20} stroke={1.5} />
      </button>
    </article>
  );
}

function Process() {
  const containerRef = useRef(null);
  const titleRef = useRef(null);

  useGSAP(() => {
    const grid = containerRef.current.querySelector(".card-grid");

    const mm = gsap.matchMedia();
    mm.add(
      {
        isDesktop: "(min-width: 900px)",
        isMobile: "(max-width: 899px)",
      },
      (context) => {
        let { isMobile } = context.conditions;

        gsap.fromTo(
          grid,
          {
            y: isMobile ? 50 : 100,
            scale: isMobile ? 0.95 : 0.9,
            filter: "blur(20px)",
            autoAlpha: 0,
          },
          {
            scrollTrigger: {
              trigger: containerRef.current,
              start: isMobile ? "top 85%" : "top 100%",
              end: "top 50%",
              scrub: 1,
            },
            y: 0,
            scale: 1,
            filter: "blur(0px)",
            autoAlpha: 1,
            ease: "none",
          }
        );
      }
    );

    // Parallax title animation - scrolls slower and blurs when overlapped
    if (titleRef.current) {
      gsap.to(titleRef.current, {
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 50%",
          end: "top -20%",
          scrub: true,
        },
        y: 150,
        filter: "blur(20px)",
        opacity: 0,
        ease: "none",
      });
    }
  }, { scope: containerRef });

  return (
    <div className="section process" ref={containerRef}>
      <h2 className="section-title" ref={titleRef}>Process</h2>

      <div className="card-grid">
        {steps.map((step) => (
          <ProcessCard key={step.label} step={step} />
        ))}
      </div>
    </div>
  );
}

export default Process;
